import { type ReactElement, useContext } from 'react'

import { useAtomValue } from 'jotai'

import type { LinkUrl, LinksLinkComponent } from '@twisto/api-cms'
import { Link as DefaultLink } from '@twisto/components/atoms/link'
import { Typography } from '@twisto/components/atoms/typography'
import { useIsMobile } from '@twisto/hooks'
import { ChevronDownIcon, ChevronRightIcon } from '@twisto/icons'

import { getLinkUrl } from 'homepage/utils'

import { navigationThemeAtom } from '../../navigation/navigation-theme'
import { NestedLinksContext } from '../../navigation/nested-links-provider'

import * as styles from './dropdown-link-holder.styles'

type DropdownLinkHolderProps = {
  children: ReactElement | null
  link?: LinksLinkComponent
  fullDropdown?: boolean
}

export const DropdownLinkHolder = ({
  fullDropdown,
  link,
  children,
}: DropdownLinkHolderProps) => {
  const isMobile = useIsMobile()
  const { openList, currentActiveListId } = useContext(NestedLinksContext) ?? {}
  const navigationTheme = useAtomValue(navigationThemeAtom)

  if (!link) {
    return null
  }

  const {
    id,
    color,
    fontWeight,
    hover,
    newTab,
    text,
    underline,
    url,
    variant,
  } = link

  const handleClick = () => {
    if (openList && id) {
      openList(id)
    }
  }

  if (currentActiveListId && currentActiveListId === id) {
    return children
  }

  return (
    <li
      css={[
        styles.container,
        currentActiveListId && currentActiveListId !== id
          ? styles.hidden
          : undefined,
      ]}
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          handleClick()
        }
      }}
    >
      {isMobile ? (
        <span css={[styles.link, fullDropdown && styles.full]}>
          <Typography component="span">{text}</Typography>

          <ChevronRightIcon css={styles.icon} size="20" />
        </span>
      ) : (
        <DefaultLink
          color="light"
          css={[
            styles.link,
            fullDropdown && styles.full,
            navigationTheme === 'dark' && styles.inverted,
          ]}
          hover={hover}
          href={url}
          target={newTab === true ? '_blank' : undefined}
          to={getLinkUrl({ url } as LinkUrl)}
          underline={underline}
          variant={variant}
        >
          <Typography color={color} component="span" fontWeight={fontWeight}>
            {text}
          </Typography>

          <ChevronDownIcon css={styles.icon} size="18" />

          {children}
        </DefaultLink>
      )}
    </li>
  )
}
